import React, { useCallback, useMemo, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

import { localEntries } from "../../shared/local-players";
import { useIsTouch } from "../../shared/useResponsive";
import WhoPlays from "../lobby/start/WhoPlays";
import PlayNowGame from "./PlayNowGame";
import styles from "./modes.module.scss";
import { ON_PORTAL } from "../../shared/portal-env";

// How many play from this device, kept for the tab so a reload does not ask again.
const WHO_KEY = "ar.playNow.who";
function savedWho(): number | null {
  try { const n = Number(sessionStorage.getItem(WHO_KEY)); return n >= 1 ? n : null; } catch { return null; }
}

// /play-now: the endless drop-in room. The room's id rides in the URL (?m=), so
// a reload lands back in the same room.
//
// With a keyboard, the way in asks "Who's playing?" (just you, or several on
// this keyboard). The start page asks before it comes here and hands the answer
// over as router state; a direct visit is asked here. A touch device is always
// one player, and so is a game site's build (CrazyGames wants players in the
// game at once; several on one keyboard is a local game in Rooms there).
export default function PlayNowPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const touch = useIsTouch();
  const [searchParams, setSearchParams] = useSearchParams();
  const [matchId] = useState(() => {
    const m = (searchParams.get("m") || "").trim();
    return /^[A-Za-z0-9_-]{1,32}$/.test(m) ? m : null;
  });
  const [who, setWho] = useState<number | null>(() => {
    const handed = Number((location.state as { who?: number } | null)?.who);
    if (handed >= 1) return handed;
    return matchId ? savedWho() : null; // a reload of a room you were in
  });
  const count = touch || ON_PORTAL ? who ?? 1 : who;
  const pick = useCallback((n: number) => { setWho(n); }, []);
  // The players as the game seats them, read once the answer is in.
  const locals = useMemo(() => {
    if (count == null) return null;
    try { sessionStorage.setItem(WHO_KEY, String(count)); } catch { /* private mode */ }
    return localEntries(count).map((p) => ({ name: p.name, colorId: p.colorId, left: p.left, right: p.right, flag: p.flag, victoryAnim: p.victoryAnim ?? null, headStyle: p.headStyle ?? null }));
  }, [count]);

  const onRoomInstance = useCallback((instance: string) => {
    const hash = instance.indexOf("#");
    if (hash <= 0) return;
    const next = new URLSearchParams(window.location.search);
    next.set("m", instance.slice(hash + 1));
    setSearchParams(next, { replace: true, state: location.state });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const leave = useCallback(() => navigate("/"), [navigate]);
  return (
    <div className={styles.page}>
      {locals ? (
        <PlayNowGame matchId={matchId} onLeave={leave} onRoomInstance={onRoomInstance} localPlayers={locals} />
      ) : (
        <WhoPlays go="Play" onCancel={leave} onPick={pick} />
      )}
    </div>
  );
}
